import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Plus, Trash2, Save, Search, X, Check, FileEdit } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CreatePurchaseOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [vendors, setVendors] = useState([]);
  const [vendorSearchQuery, setVendorSearchQuery] = useState("");
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [spareParts, setSpareParts] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [itemSearchState, setItemSearchState] = useState({}); // {index: {query, showDropdown}}

  const [formData, setFormData] = useState({
    po_number: "",  // Manual PO number
    vendor_id: "",
    vendor_name: "",
    customer_po_number: "",  // Link to Work Order
    delivery_date: "",
    payment_terms: "",  // Optional now
    shipping_address: "",
    notes: "",
    authorized_signatory: "",
    items: [],
    subtotal: 0,
    tax: 0,
    total: 0,
    linked_wo_ids: []  // Multiple Work Order IDs for accessory auto-update
  });
  
  // Work Order search state
  const [woSearchQuery, setWoSearchQuery] = useState("");
  const [showWoDropdown, setShowWoDropdown] = useState(false);
  const [selectedWOs, setSelectedWOs] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  // Close vendor dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-testid="vendor-search-input"]') && 
          !e.target.closest('.vendor-dropdown')) {
        setShowVendorDropdown(false);
      }
      if (!e.target.closest('[data-testid="wo-search-input"]') && 
          !e.target.closest('.wo-dropdown')) {
        setShowWoDropdown(false);
      }
      // Close item search dropdowns when clicking outside
      if (!e.target.closest('[data-testid^="product-name-"]')) {
        setItemSearchState(prev => {
          const updated = {};
          Object.keys(prev).forEach(k => { updated[k] = { ...prev[k], showDropdown: false }; });
          return updated;
        });
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch current user info
      try {
        const userRes = await axios.get(`${API}/auth/me`, { headers });
        setCurrentUser(userRes.data);
        // Fetch spare parts if maintenance or admin
        const dept = (userRes.data?.department || '').toLowerCase();
        if (dept === 'maintenance') {
          try {
            const spRes = await axios.get(`${API}/spare-parts`, { headers });
            setSpareParts(spRes.data);
          } catch (e) { console.log("Could not fetch spare parts"); }
        }
      } catch (e) { console.log("Could not fetch user info"); }

      // Fetch vendors first (always required)
      const vendorsRes = await axios.get(`${API}/vendors`, { headers });
      setVendors(vendorsRes.data);

      // Try to fetch work orders (may fail for maintenance department)
      try {
        const workOrdersRes = await axios.get(`${API}/work-orders`, { headers });
        setWorkOrders(workOrdersRes.data);
      } catch (woErr) {
        // Maintenance department doesn't have access to work orders - this is OK
        console.log("Work orders not accessible for this user - continuing without WO linking");
        setWorkOrders([]);
      }

      if (isEdit) {
        const poRes = await axios.get(`${API}/purchase-orders/${id}`, { headers });
        const po = poRes.data;
        setFormData({
          vendor_id: po.vendor_id,
          vendor_name: po.vendor_name,
          customer_po_number: po.customer_po_number || "",
          delivery_date: po.delivery_date,
          payment_terms: po.payment_terms,
          shipping_address: po.shipping_address,
          notes: po.notes,
          authorized_signatory: po.authorized_signatory || "",
          items: po.items,
          subtotal: po.subtotal,
          tax: po.tax,
          total: po.total,
          linked_wo_ids: po.linked_wo_ids || (po.linked_wo_id ? [po.linked_wo_id] : [])
        });
        // Populate selected WOs from linked IDs
        const linkedIds = po.linked_wo_ids || (po.linked_wo_id ? [po.linked_wo_id] : []);
        if (linkedIds.length > 0) {
          const matchedWOs = wosRes.data.filter(wo => linkedIds.includes(wo.id) || linkedIds.includes(wo.wo_number));
          setSelectedWOs(matchedWOs);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      setLoading(false);
    }
  };

  // Filter vendors based on search query
  const filteredVendors = vendors.filter(vendor => 
    vendor.name?.toLowerCase().includes(vendorSearchQuery.toLowerCase()) ||
    vendor.address?.toLowerCase().includes(vendorSearchQuery.toLowerCase()) ||
    vendor.contact?.toLowerCase().includes(vendorSearchQuery.toLowerCase())
  );

  const handleVendorSelect = (vendor) => {
    setFormData({
      ...formData,
      vendor_id: vendor.id,
      vendor_name: vendor.name,
      shipping_address: vendor.address
    });
    setVendorSearchQuery("");
    setShowVendorDropdown(false);
  };

  const clearVendor = () => {
    setFormData({
      ...formData,
      vendor_id: "",
      vendor_name: "",
      shipping_address: ""
    });
    setVendorSearchQuery("");
  };

  // Filter work orders based on search (exclude already selected)
  const filteredWOs = workOrders.filter(wo => {
    const alreadySelected = selectedWOs.some(s => s.id === wo.id);
    if (alreadySelected) return false;
    return (
      wo.wo_number?.toLowerCase().includes(woSearchQuery.toLowerCase()) ||
      wo.customer_name?.toLowerCase().includes(woSearchQuery.toLowerCase()) ||
      wo.customer_po_number?.toLowerCase().includes(woSearchQuery.toLowerCase())
    );
  });

  const handleWOSelect = (wo) => {
    const newSelectedWOs = [...selectedWOs, wo];
    setSelectedWOs(newSelectedWOs);
    setFormData({
      ...formData,
      linked_wo_ids: newSelectedWOs.map(w => w.id),
      customer_po_number: wo.customer_po_number || formData.customer_po_number || ""
    });
    setWoSearchQuery("");
    setShowWoDropdown(false);
  };

  const removeLinkedWO = (woId) => {
    const newSelectedWOs = selectedWOs.filter(w => w.id !== woId);
    setSelectedWOs(newSelectedWOs);
    setFormData({
      ...formData,
      linked_wo_ids: newSelectedWOs.map(w => w.id),
      customer_po_number: newSelectedWOs.length > 0 ? (newSelectedWOs[newSelectedWOs.length - 1].customer_po_number || formData.customer_po_number) : ""
    });
  };

  const handleVendorChange = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    if (vendor) {
      setFormData({
        ...formData,
        vendor_id: vendorId,
        vendor_name: vendor.name,
        shipping_address: vendor.address
      });
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        { product_name: "", quantity: "", unit: "pcs", unit_price: "", tax_rate: "", tax_amount: 0, total: 0 }
      ]
    });
  };

  const isMaintenance = currentUser?.department?.toLowerCase() === 'maintenance';

  const selectSparePart = (index, part) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], product_name: part.name, unit: part.unit || 'pcs' };
    setFormData({ ...formData, items: newItems });
    setItemSearchState({ ...itemSearchState, [index]: { query: part.name, showDropdown: false } });
  };

  const autoCreateSparePart = async (index, name) => {
    if (!name.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/spare-parts`, {
        name: name.trim(),
        description: "",
        opening_qty: 0,
        unit: formData.items[index]?.unit || "pcs",
        min_stock_level: 0,
        location: ""
      }, { headers: { Authorization: `Bearer ${token}` } });
      // Refresh spare parts list
      const spRes = await axios.get(`${API}/spare-parts`, { headers: { Authorization: `Bearer ${token}` } });
      setSpareParts(spRes.data);
    } catch (e) {
      // Part might already exist, that's fine
    }
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
    calculateTotals(newItems);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    
    if (field === "product_name") {
      newItems[index].product_name = value;
    } else if (field === "unit") {
      newItems[index].unit = value;
    } else if (field === "quantity") {
      newItems[index].quantity = value;
      const qty = parseFloat(value) || 0;
      const price = parseFloat(newItems[index].unit_price) || 0;
      const taxRate = parseFloat(newItems[index].tax_rate) || 0;
      const itemSubtotal = qty * price;
      newItems[index].tax_amount = itemSubtotal * (taxRate / 100);
      newItems[index].total = itemSubtotal + newItems[index].tax_amount;
    } else if (field === "unit_price") {
      newItems[index].unit_price = value;
      const qty = parseFloat(newItems[index].quantity) || 0;
      const price = parseFloat(value) || 0;
      const taxRate = parseFloat(newItems[index].tax_rate) || 0;
      const itemSubtotal = qty * price;
      newItems[index].tax_amount = itemSubtotal * (taxRate / 100);
      newItems[index].total = itemSubtotal + newItems[index].tax_amount;
    } else if (field === "tax_rate") {
      newItems[index].tax_rate = value;
      const qty = parseFloat(newItems[index].quantity) || 0;
      const price = parseFloat(newItems[index].unit_price) || 0;
      const taxRate = parseFloat(value) || 0;
      const itemSubtotal = qty * price;
      newItems[index].tax_amount = itemSubtotal * (taxRate / 100);
      newItems[index].total = itemSubtotal + newItems[index].tax_amount;
    }

    setFormData({ ...formData, items: newItems });
    calculateTotals(newItems);
  };

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return sum + (qty * price);
    }, 0);
    const tax = items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
    const total = subtotal + tax;
    setFormData(prev => ({ ...prev, subtotal, tax, total }));
  };

  const handleSubmit = async (e, saveAsDraft = false) => {
    e.preventDefault();

    if (!formData.vendor_id) {
      alert("Please select a vendor");
      return;
    }

    // For drafts, we allow empty items
    if (!saveAsDraft && formData.items.length === 0) {
      alert("Please add at least one item");
      return;
    }

    // Validate all items have product names (only for non-drafts)
    if (!saveAsDraft) {
      const invalidItems = formData.items.filter(item => !item.product_name || item.product_name.trim() === "");
      if (invalidItems.length > 0) {
        alert("Please enter product name for all items");
        return;
      }
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Prepare data with proper number conversions
      const submitData = {
        ...formData,
        status: saveAsDraft ? 'draft' : formData.status,
        items: formData.items.map(item => ({
          product_id: "",
          product_name: item.product_name || "",
          quantity: parseFloat(item.quantity) || 0,
          unit: item.unit || "pcs",
          unit_price: parseFloat(item.unit_price) || 0,
          tax_rate: parseFloat(item.tax_rate) || 0,
          tax_amount: item.tax_amount || 0,
          total: item.total || 0
        }))
      };

      let poId;
      if (isEdit) {
        await axios.put(`${API}/purchase-orders/${id}`, submitData, { headers });
        poId = id;
        if (saveAsDraft) {
          alert("✅ Purchase Order saved as draft!");
          navigate(`/po/${poId}`);
          return;
        }
      } else {
        const response = await axios.post(`${API}/purchase-orders`, submitData, { headers });
        poId = response.data.id;
        
        if (saveAsDraft) {
          alert(`✅ Draft saved: ${response.data.po_number}`);
          navigate(`/po/${poId}`);
          return;
        }
        
        // Auto-download PDF for newly created PO (not drafts)
        try {
          const pdfResponse = await axios.get(`${API}/purchase-orders/${poId}/pdf`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          });
          
          const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `PO-${response.data.po_number}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
        } catch (pdfErr) {
          console.error("Failed to download PDF:", pdfErr);
          // Don't block navigation if PDF download fails
        }
      }

      navigate('/purchase-orders');
    } catch (err) {
      console.error("Failed to save PO:", err);
      const errorMessage = err.response?.data?.detail || err.message || "Failed to save purchase order";
      alert(errorMessage);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="font-heading font-bold text-2xl sm:text-4xl tracking-tight text-primary mb-2" data-testid="create-po-title">
          {isEdit ? "Edit Purchase Order" : "Create Purchase Order"}
        </h1>
        <p className="text-muted-foreground text-sm sm:text-base">Fill in the details below</p>
      </div>

      <form onSubmit={handleSubmit} data-testid="po-form">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <div className="bg-card border border-border rounded-sm p-4 sm:p-6">
              <h2 className="font-heading font-semibold text-lg sm:text-xl mb-4">Vendor Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <label className="block text-sm font-medium mb-2">Vendor *</label>
                  
                  {/* Selected Vendor Display */}
                  {formData.vendor_id ? (
                    <div className="flex items-center justify-between px-4 py-2.5 bg-background border border-input rounded-sm">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        <span className="font-medium">{formData.vendor_name}</span>
                      </div>
                      <X 
                        className="w-4 h-4 text-muted-foreground cursor-pointer hover:text-red-500"
                        onClick={clearVendor}
                      />
                    </div>
                  ) : (
                    /* Search Input */
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="text"
                        value={vendorSearchQuery}
                        onChange={(e) => {
                          setVendorSearchQuery(e.target.value);
                          setShowVendorDropdown(true);
                        }}
                        onFocus={() => setShowVendorDropdown(true)}
                        placeholder="Search vendor by name, address..."
                        data-testid="vendor-search-input"
                        className="w-full pl-10 pr-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>
                  )}
                  
                  {/* Dropdown Results */}
                  {showVendorDropdown && !formData.vendor_id && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-input rounded-sm shadow-lg max-h-60 overflow-y-auto">
                      {filteredVendors.length === 0 ? (
                        <div className="p-3 text-sm text-muted-foreground text-center">
                          {vendorSearchQuery ? `No vendors match "${vendorSearchQuery}"` : 'No vendors available'}
                        </div>
                      ) : (
                        filteredVendors.map((vendor) => (
                          <div
                            key={vendor.id}
                            onClick={() => handleVendorSelect(vendor)}
                            className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                          >
                            <div className="font-medium text-sm">{vendor.name}</div>
                            {vendor.address && (
                              <div className="text-xs text-muted-foreground truncate">{vendor.address}</div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Delivery Date *</label>
                  <input
                    type="date"
                    value={formData.delivery_date}
                    onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                    data-testid="delivery-date-input"
                    required
                    className="w-full px-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="relative">
                  <label className="block text-sm font-medium mb-2">Link to Work Orders (for Accessory Auto-Update)</label>
                  
                  {/* Selected WOs Display as Chips */}
                  {selectedWOs.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {selectedWOs.map(wo => (
                        <div key={wo.id} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-sm">
                          <span className="font-medium text-green-800">{wo.wo_number}</span>
                          <span className="text-green-600 text-xs">{wo.customer_name}</span>
                          <X 
                            className="w-3.5 h-3.5 text-green-600 cursor-pointer hover:text-red-500 ml-1"
                            onClick={() => removeLinkedWO(wo.id)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Search Input - Always visible to add more */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={woSearchQuery}
                      onChange={(e) => {
                        setWoSearchQuery(e.target.value);
                        setShowWoDropdown(true);
                      }}
                      onFocus={() => setShowWoDropdown(true)}
                      placeholder={selectedWOs.length > 0 ? "Add another Work Order..." : "Search Work Order..."}
                      data-testid="wo-search-input"
                      className="w-full pl-10 pr-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  
                  {/* Dropdown Results */}
                  {showWoDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-input rounded-sm shadow-lg max-h-60 overflow-y-auto">
                      {filteredWOs.length === 0 ? (
                        <div className="p-3 text-sm text-muted-foreground text-center">
                          {woSearchQuery ? `No WO match "${woSearchQuery}"` : 'No more Work Orders available'}
                        </div>
                      ) : (
                        filteredWOs.slice(0, 10).map((wo) => (
                          <div
                            key={wo.id}
                            onClick={() => handleWOSelect(wo)}
                            className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                          >
                            <div className="font-medium text-sm">{wo.wo_number}</div>
                            <div className="text-xs text-muted-foreground">
                              {wo.customer_name} | PO: {wo.customer_po_number || '-'}
                            </div>
                            {wo.accessories && wo.accessories.length > 0 && (
                              <div className="text-xs text-blue-600 mt-1">
                                {wo.accessories.length} accessories: {wo.accessories.slice(0, 2).map(a => a.name).join(', ')}
                                {wo.accessories.length > 2 && ` +${wo.accessories.length - 2} more`}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Select one or more Work Orders. Accessories in linked WOs auto-update to "Ordered" when PO items match.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Payment Terms (Optional)</label>
                  <select
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    data-testid="payment-terms-select"
                    className="w-full px-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Select payment terms (optional)</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 60">Net 60</option>
                    <option value="Net 90">Net 90</option>
                    <option value="Due on Receipt">Due on Receipt</option>
                    <option value="COD">COD</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Vendor Address *</label>
                  <input
                    type="text"
                    value={formData.shipping_address}
                    onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })}
                    data-testid="shipping-address-input"
                    required
                    className="w-full px-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="Enter vendor address"
                  />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-heading font-semibold text-xl">Line Items</h2>
                <button
                  type="button"
                  onClick={addItem}
                  data-testid="add-item-button"
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-sm hover:bg-primary/90"
                >
                  <Plus size={16} />
                  Add Item
                </button>
              </div>

              {formData.items.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground" data-testid="no-items-message">
                  No items added yet. Click "Add Item" to start.
                </div>
              ) : (
                <div className="space-y-4" data-testid="items-list">
                  {formData.items.map((item, index) => (
                    <div key={index} className="border border-border rounded-sm p-4" data-testid={`item-${index}`}>
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-12 md:col-span-4">
                          <label className="block text-xs font-semibold mb-1 text-foreground">Product Name *</label>
                          {isMaintenance && spareParts.length > 0 ? (
                            <div className="relative">
                              <input
                                type="text"
                                value={itemSearchState[index]?.query ?? item.product_name}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setItemSearchState({ ...itemSearchState, [index]: { query: val, showDropdown: true } });
                                  updateItem(index, "product_name", val);
                                }}
                                onFocus={() => setItemSearchState({ ...itemSearchState, [index]: { ...itemSearchState[index], query: itemSearchState[index]?.query ?? item.product_name, showDropdown: true } })}
                                onBlur={() => {
                                  setTimeout(() => {
                                    setItemSearchState(prev => ({ ...prev, [index]: { ...prev[index], showDropdown: false } }));
                                    // Auto-create if name doesn't exist in spare parts
                                    const name = item.product_name?.trim();
                                    if (name && !spareParts.some(p => p.name.toLowerCase() === name.toLowerCase())) {
                                      autoCreateSparePart(index, name);
                                    }
                                  }, 200);
                                }}
                                data-testid={`product-name-${index}`}
                                required
                                placeholder="Search or type spare part name..."
                                className="w-full px-3 py-2 text-sm font-medium bg-background border border-input rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                              />
                              {itemSearchState[index]?.showDropdown && (
                                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                  {spareParts
                                    .filter(p => {
                                      const q = (itemSearchState[index]?.query || '').toLowerCase();
                                      return !q || p.name.toLowerCase().includes(q);
                                    })
                                    .slice(0, 30)
                                    .map(p => (
                                      <div
                                        key={p.id}
                                        className="px-3 py-2 hover:bg-muted cursor-pointer text-sm border-b last:border-0"
                                        onMouseDown={(e) => { e.preventDefault(); selectSparePart(index, p); }}
                                      >
                                        <span className="font-medium">{p.name}</span>
                                        <span className="text-muted-foreground ml-2 text-xs">({p.current_stock || 0} {p.unit})</span>
                                      </div>
                                    ))
                                  }
                                  {spareParts.filter(p => {
                                    const q = (itemSearchState[index]?.query || '').toLowerCase();
                                    return !q || p.name.toLowerCase().includes(q);
                                  }).length === 0 && itemSearchState[index]?.query && (
                                    <div className="px-3 py-2 text-sm text-blue-600">
                                      New part: "<strong>{itemSearchState[index]?.query}</strong>" — will be auto-added to inventory
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <input
                              type="text"
                              value={item.product_name}
                              onChange={(e) => updateItem(index, "product_name", e.target.value)}
                              data-testid={`product-name-${index}`}
                              required
                              placeholder="Enter product/item name"
                              className="w-full px-3 py-2 text-sm font-medium bg-background border border-input rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          )}
                        </div>
                        <div className="col-span-4 md:col-span-2">
                          <label className="block text-xs font-medium mb-1">Quantity *</label>
                          <div className="flex gap-1">
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, "quantity", e.target.value)}
                              data-testid={`quantity-input-${index}`}
                              required
                              min="1"
                              step="0.01"
                              placeholder="Qty"
                              className="w-2/3 px-2 py-2 text-sm bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                            <select
                              value={item.unit || "pcs"}
                              onChange={(e) => updateItem(index, "unit", e.target.value)}
                              data-testid={`unit-select-${index}`}
                              className="w-1/3 px-1 py-2 text-xs bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                            >
                              <option value="pcs">Pcs</option>
                              <option value="kgs">Kgs</option>
                              <option value="mtrs">Mtrs</option>
                              <option value="sets">Sets</option>
                              <option value="ltrs">Ltrs</option>
                            </select>
                          </div>
                        </div>
                        <div className="col-span-4 md:col-span-2">
                          <label className="block text-xs font-medium mb-1">Unit Price *</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm text-muted-foreground">₹</span>
                            <input
                              type="number"
                              value={item.unit_price}
                              onChange={(e) => updateItem(index, "unit_price", e.target.value)}
                              data-testid={`unit-price-input-${index}`}
                              required
                              min="0"
                              step="0.01"
                              placeholder="Price"
                              className="w-full pl-7 pr-3 py-2 text-sm bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                          </div>
                        </div>
                        <div className="col-span-4 md:col-span-2">
                          <label className="block text-xs font-medium mb-1">Tax %</label>
                          <input
                            type="number"
                            value={item.tax_rate}
                            onChange={(e) => updateItem(index, "tax_rate", e.target.value)}
                            data-testid={`tax-rate-input-${index}`}
                            min="0"
                            max="100"
                            step="0.01"
                            placeholder="Tax %"
                            className="w-full px-3 py-2 text-sm bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                        </div>
                        <div className="col-span-4 md:col-span-2">
                          <label className="block text-xs font-medium mb-1">Total</label>
                          <input
                            type="text"
                            value={item.total.toFixed(2)}
                            readOnly
                            data-testid={`item-total-${index}`}
                            className="w-full px-3 py-2 text-sm bg-muted border border-input rounded-sm font-mono"
                          />
                        </div>
                        <div className="col-span-4 md:col-span-1">
                          <label className="block text-xs font-medium mb-1">&nbsp;</label>
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            data-testid={`remove-item-${index}`}
                            className="w-full px-3 py-2 text-destructive hover:bg-destructive/10 rounded-sm transition-colors flex items-center justify-center"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-card border border-border rounded-sm p-6">
              <h2 className="font-heading font-semibold text-xl mb-4">Additional Information</h2>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Authorized Signatory</label>
                <input
                  type="text"
                  value={formData.authorized_signatory}
                  onChange={(e) => setFormData({ ...formData, authorized_signatory: e.target.value })}
                  data-testid="authorized-signatory-input"
                  className="w-full px-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Enter name of authorized person"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Additional Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="notes-textarea"
                  rows={4}
                  className="w-full px-4 py-3 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                  placeholder="Add any additional notes or instructions..."
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-card border border-border rounded-sm p-6 sticky top-8">
              <h2 className="font-heading font-semibold text-xl mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Subtotal</span>
                  <span className="font-mono font-medium" data-testid="subtotal-display">
                    ₹{formData.subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Tax (GST)</span>
                  <span className="font-mono font-medium" data-testid="tax-display">
                    ₹{formData.tax.toFixed(2)}
                  </span>
                </div>
                <div className="h-px bg-border" />
                <div className="flex justify-between items-center">
                  <span className="font-heading font-semibold text-lg">Total</span>
                  <span className="font-mono font-bold text-2xl text-primary" data-testid="total-display">
                    ₹{formData.total.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="save-po-button"
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-medium rounded-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save size={18} />
                  {saving ? "Saving..." : (isEdit ? "Update Purchase Order" : "Create Purchase Order")}
                </button>
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, true)}
                  disabled={saving}
                  data-testid="save-draft-button"
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-amber-500 text-white font-medium rounded-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileEdit size={18} />
                  {saving ? "Saving..." : "Save as Draft"}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/purchase-orders')}
                  data-testid="cancel-button"
                  className="w-full px-6 py-3 bg-secondary text-secondary-foreground font-medium rounded-sm hover:bg-secondary/80"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
